<template>
  <v-container class="member-loan-overview" fluid>
    <v-card class="pa-3 elevation-1" outlined>
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-subtitle-1 font-weight-medium">پرونده‌های اقساط من</div>
        <v-chip v-if="loans.length" color="primary" text-color="white" small pill>
          {{ loans.length }} وام
        </v-chip>
      </div>

      <v-skeleton-loader v-if="loading" type="list-item@3" class="mt-1" />

      <div v-else>
        <div v-if="!loans.length" class="text-center py-6 grey--text">
          <v-icon size="36" color="grey lighten-1" class="mb-2">mdi-bank-off-outline</v-icon>
          <div>تاکنون وامی برای شما ثبت نشده است.</div>
        </div>

        <v-expansion-panels v-else multiple accordion v-model="activePanels" class="loan-panels" @change="onPanelChange">
          <v-expansion-panel v-for="(loan, index) in loans" :key="loan.id" class="rounded-xl loan-panel">
            <div class="checkouted-mark" v-if="loan.isCheckouted">تسویه شده</div>
            <div class="checkouted-mark" v-if="loan.isCanceled">فسخ شده</div>
            <v-expansion-panel-header hide-actions class="mb-3 pa-2">
              <div class="d-flex flex-column flex-grow-1">
                <div class="grey--text ">
                  <small>شماره پرونده: {{ loan.code }}</small>
                </div>
                <div class="text-body-1 font-weight-bold">
                  {{ loan.loanModel.title }}
                </div>
                <div class="d-flex flex-wrap align-center">
                  <div class="info-pill">
                    <span class="grey--text text--darken-1">تاریخ شروع: </span>
                    <span class="font-weight-medium">{{ loan.startDate_Persian.date }}</span>
                  </div>
                  <div class="info-pill">
                    <span class="grey--text text--darken-1">مبلغ وام: </span>
                    <span class="font-weight-medium">{{ loan.amount.toString().toPriceFormat() }} <small>تومان</small></span>
                  </div>
                  <div class="info-pill">
                    <span class="grey--text text--darken-1">قسط: </span>
                    <span class="font-weight-medium">{{ loan.installment.toString().toPriceFormat() }}</span>
                  </div>
                  <div class="info-pill">
                    <span class="grey--text text--darken-1">اقساط پرداختی: </span>
                    <span class="font-weight-medium">{{ loan.paid.toString().toPriceFormat() }} <span v-if="loan.paidCount">({{ loan.paidCount }} قسط)</span></span>
                  </div>
                  <div class="info-pill">
                    <span class="grey--text text--darken-1">باقیمانده: </span>
                    <span :class="loan.remain > 0 ? 'red--text' : 'success--text'">{{ Math.abs(loan.remain).toString().toPriceFormat() }}</span>
                    <v-btn v-if="loan.remain > 0 && !loan.isCheckouted" x-small color="secondary" class="mr-2" @click.prevent="showCheckoutAmount(loan)">تسویه بدهی</v-btn>
                  </div>
                </div>
                <div class="d-flex justify-center mt-2">
                  <v-chip small label outlined color="secondary" style="min-width: 100px;">
                    {{ loan.month }} قسط {{ loan.monthGap > 1 ? `${loan.monthGap} ماهه` : 'ماهانه' }} | مشاهده جزئیات
                  </v-chip>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-row class="justify-center">
                <v-col cols="12" sm="6" md="4" xl="3" class="pa-0">
                  <div class="rounded-lg pa-2 grey lighten-5">
                    <div class="d-flex justify-space-between align-center mb-3">
                      <div class="text-subtitle-2 text-center">جزئیات اقساط</div>
                    </div>

                    <v-skeleton-loader v-if="loadingInstallments[loan.id]" type="list-item@4" />

                    <div v-else-if="installmentsMap[loan.id] ? installmentsMap[loan.id].length : 0">
                      <v-list dense class="installment-list">
                        <v-list-item v-for="inst in installmentsMap[loan.id]" :key="inst.id" class="rounded-lg mb-2 px-3">
                          <v-list-item-content>
                            <v-list-item-title class="d-flex justify-space-between align-center">
                              <div class="font-weight-bold">قسط {{ inst.installmentNumber }}</div>
                              <div class="pill small" :class="inst.delayDay > 0 ? 'red lighten-5 red--text text--darken-2' : inst.delayDay < 0 || (inst.delayDay === 0 && inst.amount) ? 'green lighten-5 green--text text--darken-2' : 'grey lighten-4 text--lighten-3'">
                                <v-icon x-small class="ml-1">{{ instIcon(inst) }}</v-icon>
                                {{ delayLabel(inst) }}
                              </div>

                            </v-list-item-title>
                            <v-list-item-subtitle class="mt-1">
                              <div class="text--primary">
                                <span class="grey--text text--darken-1">مبلغ:</span>
                                <span class="font-weight-medium mr-1">{{ inst.installmentAmount.toString().toPriceFormat() }} تومان</span>
                              </div>
                              <div class="d-flex flex-column align-start mt-2">
                                <div class="pill small">
                                  <v-icon x-small class="ml-1">mdi-calendar</v-icon>
                                  سررسید: {{ inst.targetMonth_Persian ? inst.targetMonth_Persian.date : '-' }}
                                </div>
                                <div class="pill small">
                                  <v-icon x-small class="ml-1">mdi-cash</v-icon>
                                  پرداختی: {{ inst.sumAmount.toString().toPriceFormat() }} تومان
                                </div>
                                <div class="pill small">
                                  <v-icon x-small class="ml-1">mdi-calendar</v-icon>
                                  تاریخ واریز: {{ inst.saveDate_Persian.date.isDateTime() ? inst.saveDate_Persian.date : '-' }}
                                </div>
                              </div>
                              <div class="d-flex flex-column mt-3">
                                <v-btn class="mx-2 mb-2" text small color="secondary" title="" v-if="inst.userLoanPaymentRequestId">
                                  برای این قسط اعلام واریزی ثبت کرده اید
                                </v-btn>
                                <v-btn v-else-if="inst.sumAmount < inst.installmentAmount" class="mx-2 mb-2" small color="secondary" @click="paymentRequest(inst, loan)" title="اعلام واریزی">
                                  <v-icon dark class="ml-1">
                                    mdi-thumb-up-outline
                                  </v-icon>
                                  اعلام واریزی
                                </v-btn>
                                <v-btn :disabled="!isOnlinePaymentActive || inst.sumAmount >= inst.installmentAmount" class="mx-2" small color="warning" @click="payOnline(inst, loan)" title="پرداخت آنلاین">
                                  <v-icon dark class="ml-1">
                                    mdi-credit-card-outline
                                  </v-icon>
                                  پراخت آنلاین
                                </v-btn>
                              </div>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>
                    <div v-else class="text-center grey--text py-4">
                      <v-icon size="28" color="grey lighten-1" class="mb-1">mdi-format-list-checks</v-icon>
                      <div>اقساطی برای این وام یافت نشد.</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card>

    <v-dialog v-model="dialogModifyLoanPaymentRequest" scrollable persistent max-width="900px">
      <v-card>
        <v-card-title>
          <div><span class="headline">اعلام واریزی</span></div>
          <v-spacer />
          <v-btn @click="dialogModifyLoanPaymentRequest = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <modify-loan-payment-request ref="modifyLoanPaymentRequest" :isAdmin="false" v-if="dialogModifyLoanPaymentRequest" :data="loanPaymentRequest" @callbck="callbck_loanPaymentRequest" />
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogOnlinePayment" scrollable persistent max-width="450px">
      <v-card>
        <v-card-title>
          <div><span class="headline">پرداخت آنلاین قسط</span></div>
          <v-spacer />
          <v-btn @click="dialogOnlinePayment = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <online-payment ref="onlinePayment" v-if="dialogOnlinePayment" :data="selectedLoanPayment" @callbck="callbck_onlinePayment" />
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCheckoutAmount" max-width="400px">
      <v-card>
        <v-card-title>
          <div><span class="headline">تسویه بدهی</span></div>
          <v-spacer />
          <v-btn @click="dialogCheckoutAmount = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-container>
          <div style="text-align:center">
            <div class="mb-2">مبلغ قابل تسویه:</div>
            <h3 :class="[checkoutAmount > 0 ? 'red--text' : 'success--text']">
              {{ checkoutAmount.toString().toPriceFormat() }} <span v-if="!isNaN(checkoutAmount)">{{ checkoutAmount > 0 ?
                'بدهکار' : 'بستانکار' }}</span></h3>
          </div>
          <div class="text-center my-3" v-if="checkoutAmount > 0 && isOnlinePaymentActive">
            <v-btn color="secondary" @click="checkoutOnline()" :loading="checkoutOnlineLoading">
              <v-icon dark class="ml-1">
                mdi-credit-card-outline
              </v-icon>
              پرداخت آنلاین
            </v-btn>
          </div>
        </v-container>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
  import api from '@/util/app-api'
  import toast from '@/util/toast'
  import { emptyId } from '@/util/emptyId'
  import { get } from 'vuex-pathify'

  export default {
    name: 'MemberLoanOverview',
    components: {
      'modify-loan-payment-request': () => import('@/components/forms/ModifyLoanPaymentRequest.vue'),
      'online-payment': () => import('@/components/forms/OnlinePayment.vue'),
    },
    data() {
      return {
        loading: true,
        loans: [],
        installmentsMap: {},
        loadingInstallments: {},
        dialogOnlinePayment: false,
        loanPaymentRequest: null,
        dialogModifyLoanPaymentRequest: false,
        activePanels: [],
        selectedLoanPayment: null,
        selectedLoan: null,
        checkoutOnlineLoading: false,
        dialogCheckoutAmount: false,
        checkoutAmount: 0
      }
    },
    mounted() {
      this.loadLoans()
    },
    computed: {
      ...get('memberAppSettings', [
        'isOnlinePaymentActive',
      ])
    },
    methods: {
      callbck_loanPaymentRequest(result) {
        this.dialogModifyLoanPaymentRequest = false
        this.loadInstallments(this.selectedLoanPayment.userLoanModel)
      },
      callbck_onlinePayment(result) {
        this.dialogOnlinePayment = false
        this.loadInstallments(this.selectedLoanPayment.userLoanModel)
      },
      loadLoans() {
        this.loading = true
        api.get('member/userloan/get', null)
          .then(response => {
            this.loans = response || []
          })
          .catch(err => {
            toast.error(err.message)
          })
          .finally(() => {
            this.loading = false
          })
      },
      onPanelChange(active) {
        (active || []).forEach(idx => {
          const loan = this.loans[idx]
          if (loan && !this.installmentsMap[loan.id] && !this.loadingInstallments[loan.id]) {
            this.loadInstallments(loan)
          }
        })
      },
      loadInstallments(loan) {
        this.$set(this.loadingInstallments, loan.id, true)
        api.get('admin/loanpayment/getLoanPayments/' + loan.id, null)
          .then(response => {
            this.$set(this.installmentsMap, loan.id, response || [])
          })
          .catch(err => {
            toast.error(err.message)
          })
          .finally(() => {
            this.$set(this.loadingInstallments, loan.id, false)
          })
      },
      instIcon(inst) {
        if (!inst) return 'mdi-check'
        if (inst.delayDay === 0 && inst.amount === 0) return 'mdi-timer-sand'
        if (inst.delayDay === 0) return 'mdi-check'
        if (inst.delayDay > 0) return 'mdi-danger'
        return 'mdi-check'
      },
      delayLabel(inst) {
        if (!inst) return 'بدون تاخیر'
        if (inst.delayDay === 0 && inst.amount === 0) return 'سررسید نشده'
        if (inst.delayDay === 0) return 'بدون تاخیر'
        const prefix = inst.delayDay > 0 ? 'دیرکرد' : 'زودکرد'
        return `${prefix}: ${Math.abs(inst.delayDay)} روز (${inst.delayPrice.toString().toPriceFormat()} تومان)`
      },
      checkoutOnline() {
        if (this.checkoutAmount <= 0) {
          toast.error('مبلغ قابل تسویه باید بیشتر از 0 باشد.')
          return
        }

        // ابتدا اولین قسط باز در غیر اینصورت آخرین قسط را برای پرداخت انتخاب کن
        let paymentArr = this.installmentsMap[this.selectedLoan.id] || []
        let payment = paymentArr.find(x => x.amount === 0) || paymentArr[paymentArr.length - 1]

        let data = {
          amount: this.checkoutAmount,
          userLoanPayment: payment,
          memberPurchaseType: 5,
          customerCode: 0
        }
        this.checkoutOnlineLoading = true
        let url = 'member/purchase/create'
        api.post(url, data)
          .then(response => {
            if (response.gateway === 'sep') {
              let form = document.getElementById('sepForm')
              let sepToken = document.getElementById('sepToken')
              sepToken.value = response.token
              form.submit()
            }
            else if (response.gateway === 'sepehr') {
              let form = document.getElementById('sepehrForm')
              let sepehrToken = document.getElementById('sepehrToken')
              sepehrToken.value = response.token
              let sepehrTerminal = document.getElementById('sepehrTerminalId')
              sepehrTerminal.value = response.terminalId
              form.submit()
            }
            else {
              window.location.replace(response.gatewayUrl)
            }
          })
          .catch(err => {
            this.checkoutOnlineLoading = false
            toast.error(err.message)
          })
      },
      payOnline(inst, loan) {
        if (this.installmentsMap[loan.id].findIndex(x => x.installmentNumber < inst.installmentNumber && x.sumAmount === 0) > -1) {
          toast.error('ابتدا باید سررسید های قبلی پرداخت شود.')
          return
        }
        api.get('member/LoanPayment/get/' + inst.id, null)
          .then(
            response => {
              this.selectedLoanPayment = { payment: response, userLoanModel: loan }
              this.dialogOnlinePayment = true
            })
          .catch(err => {
            toast.error(err.message)
          })
      },
      paymentRequest(inst, loan) {
        if (this.installmentsMap[loan.id].findIndex(x => x.installmentNumber < inst.installmentNumber && !x.userLoanPaymentRequestId) > -1) {
          toast.error('ابتدا باید سررسید های قبلی اعلام واریزی شود.')
          return
        }
        inst.userLoanModel = loan
        this.loanPaymentRequest = { id: emptyId, loanPayment: inst, amount: loan.installment }
        this.selectedLoanPayment = { payment: inst, userLoanModel: loan }
        this.dialogModifyLoanPaymentRequest = true
      },
      showCheckoutAmount(loan) {
        this.dialogCheckoutAmount = true
        this.selectedLoan = loan
        this.checkoutAmount = "در حال دریافت اطلاعات..."
        api.get('member/userloan/getCheckoutAmount/' + loan.id, null)
          .then(
            response => {
              this.checkoutAmount = response
            })
          .catch(err => {
            toast.error(err.message)
          })
      },
    },
  }
</script>

<style>
  .member-loan-overview {
    padding: 0;
  }

  .loan-panels .v-expansion-panel {
    border: 1px solid #f1f1f1;
    margin-bottom: 8px;
    position: relative;
    overflow: hidden;
  }

  .info-pill {
    background: #eeedff;
    border-radius: 12px;
    padding: 4px 8px;
    margin: 2px 6px 2px 0;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
  }

  .pill {
    background: #fff;
    border-radius: 12px;
    padding: 4px 8px;
    margin-top: 1px;
    margin-right: 6px;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
  }

  .pill.small {
    padding: 2px 10px;
  }

  .pill.grey.text--lighten-3 {
    color: #939393;
  }

  .installment-list {
    background-color: transparent !important;
  }

  .installment-list .v-list-item {
    background-color: #fff;
    border: 1px solid #ececec;
  }

  .v-expansion-panel-content .v-expansion-panel-content__wrap {
    padding: 0 10px !important;
  }
</style>
